import { useId, useMemo } from 'react';
import { Sparkles, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useProducts } from '@/hooks';
import { useProductSelectionStore } from '@/stores';
import { useCanvasStateManager } from '@/stores/canvasStateManager';
import { usePublishStore } from '@/stores/publishStore';
import { startAiProductDescriptionGeneration } from '@/services/aiProductDescription';
import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';

function AiProposalBadge() {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center gap-1.5 rounded-chip bg-primary-50 px-2.5 py-0.5 text-[11px] font-bold text-primary">
      <Sparkles className="h-3 w-3" aria-hidden="true" />
      {t('creator.publish.aiBadge')}
    </span>
  );
}

function FieldHint({ children }: { children: React.ReactNode }) {
  return (
    <p className="mt-2 flex items-start gap-1.5 text-xs leading-relaxed text-[var(--mute)]">
      <Sparkles
        className="mt-0.5 h-3 w-3 shrink-0 text-primary-300"
        aria-hidden="true"
      />
      <span>{children}</span>
    </p>
  );
}

/**
 * Product information form for Publish step.
 *
 * The AI suggestion ran once when the seller reached the products step; the
 * button here repeats it on demand and always replaces all three fields.
 */
export function ProductInfoForm() {
  const { t } = useTranslation();
  const title = usePublishStore((state) => state.title);
  const description = usePublishStore((state) => state.description);
  const tags = usePublishStore((state) => state.tags);
  const tagInput = usePublishStore((state) => state.tagInput);
  const aiGenerationStatus = usePublishStore(
    (state) => state.aiGenerationStatus
  );
  const setTitle = usePublishStore((state) => state.setTitle);
  const setDescription = usePublishStore((state) => state.setDescription);
  const setTagInput = usePublishStore((state) => state.setTagInput);
  const addTag = usePublishStore((state) => state.addTag);
  const removeTag = usePublishStore((state) => state.removeTag);

  // The suggestion needs at least one position with objects. Read from the
  // saved position state: the canvas is unmounted on this step.
  const hasDesign = useCanvasStateManager(
    (state) => state.getPositionsWithDesigns().length > 0
  );

  const { data: apiProducts } = useProducts();
  const selections = useProductSelectionStore((state) => state.selections);
  const focusedProductId = useProductSelectionStore(
    (state) => state.focusedProductId
  );

  const hintId = useId();

  const exampleProductName = useMemo(() => {
    const fallback = t('creator.publish.exampleProductName');
    if (!apiProducts) return fallback;
    const activeProducts = apiProducts.filter(
      (product) => selections[product.id]?.isActive
    );
    const mainProduct = focusedProductId
      ? activeProducts.find((product) => product.id === focusedProductId)
      : activeProducts[0];
    return mainProduct?.name || fallback;
  }, [apiProducts, selections, focusedProductId, t]);

  const hasTitle = title.trim().length > 0;
  const isGenerating = aiGenerationStatus === 'loading';
  const hasSuggestion = aiGenerationStatus === 'success';
  const hasFailed = aiGenerationStatus === 'error';
  const showTitleError = !hasTitle && !isGenerating;
  const exampleTitle = title.trim() || t('creator.publish.exampleTitle');

  const fieldInputClass =
    'dc-field w-full rounded-control border-none bg-[var(--paper)] ring-1 ring-inset ring-[var(--line)] px-3.5 py-3 text-[15px] text-[var(--ink)] placeholder-[var(--mute)] focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-[var(--paper-2)]';

  return (
    <section className="overflow-hidden rounded-card border border-[var(--line)] bg-[var(--paper-2)]">
      <div className="space-y-6 p-6">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => void startAiProductDescriptionGeneration()}
              disabled={!hasDesign}
              loading={isGenerating}
              loadingLabel={t('creator.publish.aiGenerating')}
              aria-describedby={hasDesign ? undefined : hintId}
            >
              <Sparkles className="h-4 w-4" aria-hidden="true" />
              {t(
                hasSuggestion
                  ? 'creator.publish.aiSuggestAgain'
                  : 'creator.publish.aiSuggest'
              )}
            </Button>
            {!hasDesign && (
              <p id={hintId} className="text-xs text-[var(--mute)]">
                {t('creator.publish.aiNeedsDesign')}
              </p>
            )}
          </div>

          <p
            role="status"
            aria-live="polite"
            className={cn(
              'text-xs',
              hasFailed &&
                'rounded-control border border-warning bg-[color-mix(in_srgb,var(--warning)_10%,transparent)] px-3 py-2 text-warning',
              hasSuggestion && 'text-[var(--mute)]',
              !hasFailed && !hasSuggestion && 'sr-only'
            )}
          >
            {hasFailed
              ? t('creator.publish.aiFailed')
              : hasSuggestion
                ? t('creator.publish.aiApplied')
                : ''}
          </p>
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2.5">
            <label
              htmlFor="design-title"
              className="dc-type-label text-[var(--mute)]"
            >
              {t('creator.publish.titleLabel')}
            </label>
            {hasSuggestion && <AiProposalBadge />}
          </div>

          <input
            id="design-title"
            type="text"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            disabled={isGenerating}
            className={cn(
              fieldInputClass,
              showTitleError && 'ring-2 ring-[var(--danger)]'
            )}
            placeholder={t('creator.publish.titlePlaceholder')}
            aria-invalid={showTitleError}
          />

          {showTitleError ? (
            <p className="text-xs text-[var(--danger)]">
              {t('creator.publish.titleRequired')}
            </p>
          ) : (
            <FieldHint>
              {t('creator.publish.titleHint', {
                title: exampleTitle,
                product: exampleProductName,
              })}
            </FieldHint>
          )}
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2.5">
            <label
              htmlFor="design-description"
              className="dc-type-label text-[var(--mute)]"
            >
              {t('creator.publish.descriptionLabel')}
            </label>
            {hasSuggestion && <AiProposalBadge />}
          </div>

          <textarea
            id="design-description"
            rows={5}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            disabled={isGenerating}
            className={cn(fieldInputClass, 'resize-y leading-relaxed')}
            placeholder={t('creator.publish.descriptionPlaceholder')}
          />

          <FieldHint>{t('creator.publish.descriptionHint')}</FieldHint>
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2.5">
            <label
              htmlFor="design-tags"
              className="dc-type-label text-[var(--mute)]"
            >
              {t('creator.publish.tagsLabel')}
            </label>
            {hasSuggestion && <AiProposalBadge />}
          </div>

          <div className="dc-field flex min-h-[52px] flex-wrap items-center gap-2 rounded-control border border-[var(--line)] bg-[var(--paper)] px-3.5 py-3">
            {tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1.5 rounded-chip bg-primary-100 px-3 py-1.5 text-[13px] font-semibold text-[var(--ink)] font-mono"
              >
                {tag}
                <button
                  type="button"
                  onClick={() => removeTag(tag)}
                  disabled={isGenerating}
                  className="opacity-55 hover:opacity-100 disabled:opacity-30"
                  aria-label={t('creator.publish.removeTag', { tag })}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
            <input
              id="design-tags"
              type="text"
              value={tagInput}
              onChange={(event) => setTagInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ',') {
                  event.preventDefault();
                  addTag(tagInput);
                }
              }}
              onBlur={() => addTag(tagInput)}
              disabled={isGenerating}
              placeholder={t('creator.publish.tagPlaceholder')}
              className="min-w-[130px] flex-1 border-none bg-transparent text-sm text-[var(--ink)] placeholder-[var(--mute)] focus:outline-none"
            />
          </div>

          <FieldHint>{t('creator.publish.tagsHint')}</FieldHint>
        </div>
      </div>
    </section>
  );
}
